import {
  logEvent as logEventFirebase,
  setUserId,
  setUserProperties,
} from "firebase/analytics";
import { analytics, isProduction } from "./firebase";
import { currentConfig } from "./appConfig";
// Named imports, not `import * as`: a namespace import defeats Rollup's
// tree-shaking, which kept `replayIntegration` (and all ~290KB of rrweb behind
// it) in the entry chunk even though nothing here references it statically.
// browserTracingIntegration comes from @sentry/react, not @sentry/capacitor.
// The Capacitor package ships without `sideEffects: false`, so importing
// anything from it forces its `export { replayIntegration } from
// '@sentry/browser'` re-export to be retained — pulling Session Replay back
// into the entry chunk. The two integrations are the same implementation.
import { browserTracingIntegration } from "@sentry/react";
import {
  captureException,
  consoleLoggingIntegration,
  init as sentryInit,
  setTag,
  setUser,
  makeFetchTransport,
  type BrowserOptions,
} from "@sentry/react";
import { getBuildInfo } from "./lib/buildinfo";
// this is used to ensure that the telemetry is initialized only once
let isInitialized = false;

/**
 * Session Replay pulls in ~290KB of source (rrweb + the Sentry replay
 * integration). Loading it as part of `initTelemetry` put all of it in front
 * of the very first paint, which is the whole cost on a cold ad-landing visit.
 * Instead we init Sentry immediately (so errors are captured from the first
 * line) and attach replay from its own chunk once the page is idle.
 *
 * Trade-off: the first second or so of the session is not recorded. Replays
 * attached to errors still work, because rrweb starts buffering as soon as the
 * integration is added and errors before that point are far more likely to be
 * bundle-load failures than in-app interactions.
 */
function attachSentryReplay() {
  const load = () => {
    void import("./lib/sentryReplay")
      .then(({ startSessionReplay }) => {
        startSessionReplay();
        console.log("[Telemetry] ✅ Sentry Session Replay attached");
      })
      .catch((err) => {
        console.warn("[Telemetry] Session Replay failed to load", err);
      });
  };

  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(load, { timeout: 5_000 });
  } else {
    setTimeout(load, 2_000);
  }
}

export function initTelemetry() {
  if (!isInitialized) {
    isInitialized = true;

    const config = currentConfig();

    console.log("[Telemetry] Initializing telemetry...");
    console.log(
      "[Telemetry] Platform:",
      config.isNativePlatform ? `Native (${config.getPlatform?.()})` : "Web"
    );

    // Initialize Sentry immediately - don't block on async getBuildInfo().
    // Session Replay is attached separately (see attachSentryReplay) so its
    // chunk does not sit in front of first paint.
    if (config.sentry) {
      // FIX: Use browser transport for replay on Android only
      // Android native transport doesn't support replay data - must use browser fetch transport
      // iOS works fine with native transport (HybridSDK handles replay correctly)
      const isAndroid = config.getPlatform?.() === "android";
      const useBrowserTransport =
        isAndroid && config.isNativePlatform && config.sentry.recordReplays;

      const sentryOptions: BrowserOptions = {
        enableLogs: true,
        dsn:
          config.sentry.projectId.startsWith("https://") ||
          config.sentry.projectId.startsWith("http://")
            ? config.sentry.projectId
            : `https://febd00f56660343faa903ec0c4bf1ed1@o4509719704109056.ingest.us.sentry.io/${config.sentry.projectId}`, // this is for back compat.  Apparently the dsn root is different between projects.
        sendDefaultPii: true,
        // FIX: Use browser transport when replay is enabled on Android
        // Native transport doesn't support browser replay data format
        ...(useBrowserTransport ? { transport: makeFetchTransport } : {}),
        integrations: [
          browserTracingIntegration(),
          consoleLoggingIntegration({ levels: ["warn", "error"] }),
        ],
        tracesSampleRate: 1.0,
        ...(config.sentry.recordReplays
          ? {
              replaysSessionSampleRate: 1.0,
              replaysOnErrorSampleRate: 1.0,
            }
          : {}),
        environment: isProduction() ? "production" : "staging",
      };

      // FIX: When using browser transport for replay on Android, use the React init directly
      // to ensure replay data goes through browser fetch transport instead of native transport
      if (config.sentry.nativeInit && !useBrowserTransport) {
        config.sentry.nativeInit(sentryOptions, sentryInit);
      } else {
        sentryInit(sentryOptions);
      }

      console.log(
        "[Telemetry] ✅ Sentry initialized | environment:",
        sentryOptions.environment
      );

      if (config.sentry.recordReplays) {
        attachSentryReplay();
      }

      // Fetch build info in background and attach to Sentry scope as tags.
      // This avoids blocking Sentry init (and Session Replay) on a network request.
      getBuildInfo().then((buildInfo) => {
        if (buildInfo?.version) {
          setTag("app_version", buildInfo.version);
          if (buildInfo.buildNumber) {
            setTag("build_number", buildInfo.buildNumber);
          }
          console.log(
            "[Telemetry] ✅ Build info attached to Sentry:",
            buildInfo.version
          );
        }
      });
    }
  }
}

export const logEvent = (event: string, params?: Record<string, any>) => {
  const config = currentConfig();
  // Event params in GA are limited to 100 characters
  const processedParams = params
    ? Object.fromEntries(
        Object.entries(params)
          .filter(([key, value]) => value !== undefined && value !== null)
          .map(([key, value]) => [
            key,
            typeof value === "string" && value.length > 100
              ? value.substring(0, 99) + "~"
              : value,
          ])
      )
    : undefined;

  console.log(`[Telemetry] 📊 Event: ${event}`, processedParams);

  // Log to Google Analytics (native on mobile, web on browser)
  if (config.isNativePlatform) {
    if (config.nativeAnalytics?.logEvent) {
      config.nativeAnalytics.logEvent(event, processedParams);
    }
  } else {
    if (analytics) {
      logEventFirebase(analytics, event, processedParams);
    }
  }
};

export const logUser = (id: string, email: string) => {
  const config = currentConfig();
  console.log(`[Telemetry] 👤 Setting user: ${id} (${email})`);

  // Set user ID in Google Analytics
  if (config.isNativePlatform) {
    if (config.nativeAnalytics?.setUserId) {
      config.nativeAnalytics.setUserId(id);
      console.log("[Telemetry] ✅ User ID set in Google Analytics (native)");
    }
  } else {
    if (analytics) {
      setUserId(analytics, id);
      console.log("[Telemetry] ✅ User ID set in Google Analytics (web)");
    }
  }

  // Set user in Sentry for both native and web
  if (config.sentry) {
    setUser({ id, email });
    console.log("[Telemetry] ✅ User set in Sentry");
  }
};

export const logUserProperty = (key: string, value: string) => {
  const config = currentConfig();
  console.log(`[Telemetry] 🔖 User property: ${key} = ${value}`);
  if (config.isNativePlatform) {
    if (config.nativeAnalytics?.setUserProperty) {
      config.nativeAnalytics.setUserProperty(key, value);
    }
  } else {
    if (analytics) {
      setUserProperties(analytics, { [key]: value });
    }
  }
};

export const logException = (error: unknown) => {
  if (currentConfig().sentry) {
    captureException(error);
  } else {
    console.error(error);
  }
};

// NOTE: This is for backwards compatibility with the understand app.
//       We should consider connecting this to Stentry Log Tag but we need to audit the usage of logTag in Understand first.
export const logTag = (tag: string, value: string) => {
  console.log(`[Telemetry] 🔖 Tag: ${tag} = ${value}`);
};
